import type { EmailScoringFlags } from "@/lib/tools/formatters";

export function asRecord(
  value: unknown,
): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
}

/** First defined value among v2/v1 (or nested) keys on `source`. */
export function pickField(
  source: Record<string, unknown> | undefined,
  ...keys: string[]
): unknown {
  if (!source) {
    return undefined;
  }
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
}

export function pickBoolean(
  source: Record<string, unknown> | undefined,
  ...keys: string[]
): boolean | undefined {
  const value = pickField(source, ...keys);
  return typeof value === "boolean" ? value : undefined;
}

export function pickString(
  source: Record<string, unknown> | undefined,
  ...keys: string[]
): string | undefined {
  const value = pickField(source, ...keys);
  if (value === undefined || value === null) {
    return undefined;
  }
  return String(value);
}

// --- BIN ---

export function binInfo(data: Record<string, unknown>) {
  return asRecord(data.info);
}

export function binScheme(data: Record<string, unknown>) {
  return asRecord(data.scheme) ?? asRecord(binInfo(data)?.scheme);
}

export function binBank(data: Record<string, unknown>) {
  return asRecord(data.bank) ?? asRecord(binInfo(data)?.bank);
}

export function binCountry(data: Record<string, unknown>) {
  return asRecord(data.country) ?? asRecord(binInfo(data)?.country);
}

export function binIsValid(data: Record<string, unknown>) {
  return pickBoolean(data, "is_valid", "isValid") === true;
}

// --- ASN ---

export function asnNumber(data: Record<string, unknown>) {
  return pickString(data, "number", "asn");
}

export function asnCountryCode(data: Record<string, unknown>) {
  return pickString(data, "country_code", "country");
}

// --- Email ---

export function emailScoringFlags(
  data: Record<string, unknown>,
): EmailScoringFlags {
  const domain = asRecord(data.domain);
  return {
    is_valid: pickBoolean(data, "is_valid", "isValid"),
    is_free: pickBoolean(data, "is_free", "isFree"),
    is_role_based: pickBoolean(data, "is_role_based", "isRoleBased"),
    is_disposable: pickBoolean(data, "is_disposable", "isDisposable"),
    is_educational: pickBoolean(data, "is_educational", "isEducational"),
    domain_is_new: pickBoolean(domain, "is_new"),
  };
}

// --- IBAN ---

export function ibanCountry(data: Record<string, unknown>) {
  return asRecord(data.country);
}

export function ibanCentralBank(country: Record<string, unknown> | undefined) {
  return asRecord(country?.central_bank) ?? asRecord(country?.centralBank);
}

// --- Profanity ---

export function profanityRiskLevel(data: Record<string, unknown>) {
  const level = pickString(data, "risk_level", "riskLevel");
  if (level) {
    return level;
  }
  const score = pickField(data, "riskScore");
  if (score !== undefined && score !== null) {
    return String(score);
  }
  return undefined;
}

export function profanityRecommendation(data: Record<string, unknown>) {
  return pickString(data, "recommendation");
}

// --- IP ---

export function ipAddress(data: Record<string, unknown>) {
  const ip = data.ip;
  if (typeof ip === "string") {
    return ip;
  }
  return pickString(asRecord(ip), "address");
}

export function ipNumeric(data: Record<string, unknown>) {
  const ip = asRecord(data.ip);
  const fromIp = pickField(ip, "numeric");
  if (fromIp !== undefined && fromIp !== null) {
    return String(fromIp);
  }
  const legacy = pickField(data, "IPNumber");
  return legacy !== undefined && legacy !== null ? String(legacy) : undefined;
}

export function ipVersionLabel(
  data: Record<string, unknown>,
  fallback: string,
) {
  const ip = asRecord(data.ip);
  const version = pickField(ip, "version");
  if (version === 4 || version === 6) {
    return `IPv${version}`;
  }
  const ipType = pickString(data, "ipType");
  if (ipType) {
    return ipType;
  }
  const address = ipAddress(data);
  if (address?.includes(":")) {
    return "IPv6";
  }
  if (address) {
    return "IPv4";
  }
  return fallback;
}

export function ipGeo(
  data: Record<string, unknown>,
  v2Key: string,
  v1Key: string,
) {
  const geo = asRecord(data.geo);
  const fromGeo = pickField(geo, v2Key);
  if (fromGeo !== undefined && fromGeo !== null) {
    return fromGeo;
  }
  return pickField(data, v1Key);
}

export function ipLocationPhone(data: Record<string, unknown>) {
  const location = asRecord(data.location);
  return (
    pickField(location, "phone") ?? pickField(location, "phoneCode") ?? undefined
  );
}

export function ipCurrency(
  data: Record<string, unknown>,
  v2Key: string,
  v1Key: string,
) {
  const currency = asRecord(data.currency);
  const fromCurrency = pickField(currency, v2Key);
  if (fromCurrency !== undefined && fromCurrency !== null) {
    return fromCurrency;
  }
  return pickField(currency, v1Key);
}

export function ipAsnNumber(data: Record<string, unknown>) {
  const asn = asRecord(data.asn);
  return pickString(asn, "number", "asn");
}

export function ipTimezone(
  data: Record<string, unknown>,
  ...keys: string[]
) {
  const timezone = asRecord(data.timezone);
  return pickField(timezone, ...keys);
}

export function ipSunInfo(data: Record<string, unknown>) {
  const timezone = asRecord(data.timezone);
  return asRecord(timezone?.sun_info) ?? asRecord(timezone?.sunInfo);
}
